import compact from 'lodash/compact';
import { useCallback, useMemo } from 'react';
import { useUpdateEffect } from 'react-use';

import { Bean } from '@dreryk/react-common';

import { FormsTemplateInitialEditListItem, FormsTemplateListItem } from '../FormsTemplates.types';
import { FormsTemplatesContext, FormsTemplatesContextProvider } from '../formsTemplatesContext';
import { EMPTY_TEMPLATE_ID } from '../list/FormsTemplatesListItemsRenderer';

import {
   createAddNewItemAction,
   createDisableSwitchingAction,
   createEnableSwitchingAction,
   createRemoveItemAction,
   createSetActiveTemplateNameAction,
   createSetEditItemAction,
   createSetItemsAction,
   createSetReadOnlyItemAction,
   useFormsTemplatesApiRef,
   useFormsTemplatesReducer,
} from './FormsTemplatesState.hooks';

type FormsTemplatesStateProps<ITEM extends Bean> = {
   readonly initialEditItem: FormsTemplateInitialEditListItem<ITEM> | undefined;
   readonly templates: FormsTemplateListItem<ITEM>[];
   readonly children: JSX.Element;
};

// eslint-disable-next-line max-lines-per-function
export const FormsTemplatesState = <ITEM extends Bean>(props: FormsTemplatesStateProps<ITEM>): JSX.Element => {
   const templatesApiRef = useFormsTemplatesApiRef();

   const [{ activeItem, items, activeItemName, switchingDisabled, mode }, dispatchAction] = useFormsTemplatesReducer(
      props.templates,
      props.initialEditItem
   );

   const dispatchSetItemsAction = useCallback(
      (items: FormsTemplateListItem<ITEM>[]) => dispatchAction(createSetItemsAction(items)),
      [dispatchAction]
   );
   const dispatchEnableSwitchingAction = useCallback(() => dispatchAction(createEnableSwitchingAction()), [
      dispatchAction,
   ]);
   const dispatchDisableSwitchingAction = useCallback(() => dispatchAction(createDisableSwitchingAction()), [
      dispatchAction,
   ]);
   const dispatchSetActiveItemNameAction = useCallback(
      (templateName: string) => dispatchAction(createSetActiveTemplateNameAction(templateName)),
      [dispatchAction]
   );
   const setReadOnlyItem = useCallback(
      (item: FormsTemplateListItem<ITEM> | undefined): void => {
         const activeItemEmpty = activeItem?.id === EMPTY_TEMPLATE_ID;
         const sameItem = activeItem?.id === item?.id;

         if (sameItem) {
            templatesApiRef.current?.onActiveItemClick();
         }

         if (activeItemEmpty && sameItem) {
            return;
         }
         dispatchAction(createSetReadOnlyItemAction(item));
      },
      [activeItem?.id, dispatchAction, templatesApiRef]
   );
   const setEditItem = useCallback(
      (item: FormsTemplateListItem<ITEM>): void => {
         dispatchAction(createSetEditItemAction(item));
      },
      [dispatchAction]
   );

   const addNewItem = useCallback(() => {
      dispatchAction(createAddNewItemAction());
   }, [dispatchAction]);

   const removeItem = useCallback(
      (itemId: number | string) => {
         dispatchAction(createRemoveItemAction(itemId));
      },
      [dispatchAction]
   );

   const formsTemplatesContext = (useMemo(
      () => ({
         activeItem: activeItem,
         setEditItem: setEditItem,
         setReadOnlyItem: setReadOnlyItem,
         disableSwitching: dispatchDisableSwitchingAction,
         enableSwitching: dispatchEnableSwitchingAction,
         addNewItem: addNewItem,
         activeItemName: activeItemName,
         switchingDisabled: switchingDisabled,
         mode: mode,
         templatesApiRef: templatesApiRef,
         setActiveItemName: dispatchSetActiveItemNameAction,
         items: items,
         removeItem: removeItem,
      }),
      [
         activeItem,
         activeItemName,
         addNewItem,
         dispatchDisableSwitchingAction,
         dispatchEnableSwitchingAction,
         dispatchSetActiveItemNameAction,
         mode,
         setEditItem,
         setReadOnlyItem,
         switchingDisabled,
         templatesApiRef,
         items,
         removeItem,
      ]
   ) as unknown) as FormsTemplatesContext<ITEM>;

   useUpdateEffect(() => {
      dispatchSetItemsAction(compact([activeItem?.id === EMPTY_TEMPLATE_ID && activeItem, ...props.templates]));
   }, [props.templates]);

   return <FormsTemplatesContextProvider value={formsTemplatesContext}>{props.children}</FormsTemplatesContextProvider>;
};
